<template>
  <div class="nutrition-risk-wrapper">
    <!-- Badge clicável -->
    <div
      v-if="nutritionRisk && !loading"
      class="nutrition-risk-badge mr-2"
      :class="badgeClass"
      @click="openModal"
      title="Clique para ver o detalhamento"
    >
      <i :class="badgeIcon" class="mr-1"></i>
      <span>Nutrição: {{ badgeLabel }}</span>
      <span v-if="nutritionRisk.score !== null" class="nutrition-risk-score">
        {{ nutritionRisk.score }}pts
      </span>
      <span v-else class="nutrition-risk-score">—</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="nutrition-risk-badge nutrition-risk-loading">
      <b-spinner small class="mr-1" />
      <span>Calculando risco...</span>
    </div>

    <!-- Modal de detalhamento -->
    <div v-if="showModal" class="nutrition-risk-modal" @click.self="closeModal">
      <div class="nutrition-risk-modal-content">
        <!-- Header -->
        <div class="nutrition-risk-modal-header" :class="headerClass">
          <div class="nutrition-risk-modal-header-info">
            <i class="fas fa-utensils mr-2" style="font-size: 24px"></i>
            <div>
              <h3 class="m-0">Score de Risco Nutricional</h3>
              <p class="m-0" style="opacity: 0.9; font-size: 13px">
                Última atualização: {{ updatedAt }}
              </p>
            </div>
          </div>
          <button @click="closeModal" class="nutrition-risk-close-btn">
            &times;
          </button>
        </div>

        <!-- Alerta: peso desatualizado -->
        <div v-if="detail.missing_weight" class="nutrition-missing-weight">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Peso não registrado nos últimos 90 dias. Score nutricional
          indisponível. Registre o peso do residente para habilitar o cálculo.
        </div>

        <!-- Classificação (só exibe se score disponível) -->
        <div
          v-if="!detail.missing_weight"
          class="nutrition-risk-classification"
          :class="classificationClass"
        >
          <div class="nutrition-risk-classification-icon">
            <i :class="badgeIcon"></i>
          </div>
          <div>
            <h2 class="m-0">{{ badgeLabel }}</h2>
            <p class="m-0" style="opacity: 0.9">
              Score total: {{ nutritionRisk.score }} pontos
            </p>
          </div>
        </div>

        <!-- Detalhamento dos pontos -->
        <div
          v-if="!detail.missing_weight"
          class="nutrition-risk-detail-section"
        >
          <h4 class="nutrition-risk-section-title">
            <i class="fas fa-list-ul mr-2"></i>Detalhamento da Pontuação
          </h4>

          <div class="nutrition-risk-detail-grid">
            <!-- IMC -->
            <div
              class="nutrition-risk-detail-item"
              :class="detail.imc_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-weight mr-1"></i>
                IMC
                <span v-if="detail.imc !== null" class="detail-sub">
                  ({{ detail.imc }} kg/m² — Peso atual:
                  {{ detail.weight_latest }} kg)
                </span>
              </div>
              <div
                class="detail-points"
                :class="detail.imc_points > 0 ? 'points-alert' : 'points-ok'"
              >
                +{{ detail.imc_points }}
              </div>
            </div>

            <!-- Perda de peso 30 dias -->
            <div
              class="nutrition-risk-detail-item"
              :class="weightLoss30Alert ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-chart-line mr-1"></i>
                Perda de peso em 30 dias
                <span v-if="detail.loss_30d_pct !== null" class="detail-sub">
                  ({{ detail.loss_30d_pct }}% — de {{ detail.weight_30d }}kg
                  para {{ detail.weight_latest }}kg)
                </span>
                <span v-else class="detail-sub">(sem registro anterior)</span>
              </div>
              <div
                class="detail-points"
                :class="weightLoss30Alert ? 'points-alert' : 'points-ok'"
              >
                <span v-if="weightLossApplied === '30d'">+3</span>
                <span v-else>+0</span>
              </div>
            </div>

            <!-- Perda de peso 90 dias -->
            <div
              class="nutrition-risk-detail-item"
              :class="weightLoss90Alert ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-chart-line mr-1"></i>
                Perda de peso em 90 dias
                <span v-if="detail.loss_90d_pct !== null" class="detail-sub">
                  ({{ detail.loss_90d_pct }}% — de {{ detail.weight_90d }}kg
                  para {{ detail.weight_latest }}kg)
                </span>
                <span v-else class="detail-sub">(sem registro anterior)</span>
              </div>
              <div
                class="detail-points"
                :class="weightLoss90Alert ? 'points-alert' : 'points-ok'"
              >
                <span v-if="weightLossApplied === '90d'">+5</span>
                <span v-else>+0</span>
              </div>
            </div>

            <!-- Aceitação alimentar -->
            <div
              class="nutrition-risk-detail-item"
              :class="detail.acceptance_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-utensils mr-1"></i>
                Aceitação alimentar (últimos 7 dias)
                <span
                  v-if="detail.avg_acceptance_pct !== null"
                  class="detail-sub"
                >
                  (média: {{ detail.avg_acceptance_pct }}%)
                </span>
                <span v-else class="detail-sub">(sem registros)</span>
                <span v-if="detail.absent_count > 0" class="detail-absent">
                  · {{ detail.absent_count }} refeição(ões) ausente(s)
                </span>
              </div>
              <div
                class="detail-points"
                :class="
                  detail.acceptance_points > 0 ? 'points-alert' : 'points-ok'
                "
              >
                +{{ detail.acceptance_points }}
              </div>
            </div>
          </div>

          <!-- Regra de exclusividade -->
          <div
            v-if="weightLoss30Alert && weightLoss90Alert"
            class="nutrition-exclusivity-note"
          >
            <i class="fas fa-info-circle mr-1"></i>
            Perda em 30 e 90 dias detectadas. Apenas a maior pontuação foi
            aplicada (90 dias).
          </div>

          <!-- Total -->
          <div class="nutrition-risk-total" :class="totalClass">
            <span>Score Total</span>
            <span class="nutrition-risk-total-value">
              {{ nutritionRisk.score }} pontos
            </span>
          </div>

          <!-- Legenda -->
          <div class="nutrition-risk-legend">
            <span class="legend-item legend-verde">0–2 Baixo</span>
            <span class="legend-item legend-amarelo">3–5 Moderado</span>
            <span class="legend-item legend-vermelho">6+ Alto</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="nutrition-risk-modal-footer">
          <small class="text-muted">
            <i class="fas fa-info-circle mr-1"></i>
            Score calculado com base nos dados cadastrados
          </small>
          <v-btn
            class="nutrition-risk-recalculate-btn"
            small
            :loading="recalculating"
            :disabled="recalculating"
            @click="recalculate"
          >
            <i class="fas fa-sync-alt mr-1"></i>
            Recalcular
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Overlay -->
    <div v-if="showModal" class="nutrition-risk-overlay"></div>
  </div>
</template>

<script>
import api from "../../services/api";
import moment from "moment";

export default {
  name: "NutritionRiskBadge",

  props: {
    resident_id: [String, Number],
  },

  data() {
    return {
      loading: false,
      recalculating: false,
      showModal: false,
      nutritionRisk: null,
    };
  },

  computed: {
    detail() {
      if (!this.nutritionRisk || !this.nutritionRisk.detail) return {};
      return this.nutritionRisk.detail;
    },

    // Qual janela de perda de peso foi aplicada (exclusividade)
    weightLossApplied() {
      const loss90 = this.detail.loss_90d_pct;
      const loss30 = this.detail.loss_30d_pct;
      if (loss90 !== null && loss90 >= 10) return "90d";
      if (loss30 !== null && loss30 >= 5) return "30d";
      return null;
    },

    weightLoss30Alert() {
      return this.detail.loss_30d_pct !== null && this.detail.loss_30d_pct >= 5;
    },

    weightLoss90Alert() {
      return (
        this.detail.loss_90d_pct !== null && this.detail.loss_90d_pct >= 10
      );
    },

    badgeClass() {
      if (this.detail.missing_weight) return "badge-sem-dados";
      const level = this.nutritionRisk && this.nutritionRisk.level;
      if (level === "alto") return "badge-vermelho";
      if (level === "moderado") return "badge-amarelo";
      return "badge-verde";
    },

    headerClass() {
      if (this.detail.missing_weight) return "header-sem-dados";
      const level = this.nutritionRisk && this.nutritionRisk.level;
      if (level === "alto") return "header-vermelho";
      if (level === "moderado") return "header-amarelo";
      return "header-verde";
    },

    classificationClass() {
      const level = this.nutritionRisk && this.nutritionRisk.level;
      if (level === "alto") return "classification-vermelho";
      if (level === "moderado") return "classification-amarelo";
      return "classification-verde";
    },

    totalClass() {
      const level = this.nutritionRisk && this.nutritionRisk.level;
      if (level === "alto") return "total-vermelho";
      if (level === "moderado") return "total-amarelo";
      return "total-verde";
    },

    badgeLabel() {
      if (this.detail.missing_weight) return "Sem dados";
      const level = this.nutritionRisk && this.nutritionRisk.level;
      if (level === "alto") return "Alto";
      if (level === "moderado") return "Moderado";
      return "Baixo";
    },

    badgeIcon() {
      if (this.detail.missing_weight) return "fas fa-question-circle";
      const level = this.nutritionRisk && this.nutritionRisk.level;
      if (level === "alto") return "fas fa-exclamation-triangle";
      if (level === "moderado") return "fas fa-exclamation-circle";
      return "fas fa-check-circle";
    },

    updatedAt() {
      if (!this.nutritionRisk || !this.nutritionRisk.updated_at) return "—";
      return moment(this.nutritionRisk.updated_at).format("DD/MM/YYYY HH:mm");
    },
  },

  created() {
    this.loadScore();
  },

  methods: {
    async loadScore() {
      try {
        this.loading = true;
        const response = await api.get(`/ai/smart/summary/${this.resident_id}`);
        if (response.data.success) {
          this.nutritionRisk = response.data.nutrition_risk;
        }
      } catch (error) {
        console.error("[NutritionRiskBadge] erro ao carregar score:", error);
      } finally {
        this.loading = false;
      }
    },

    async recalculate() {
      try {
        this.recalculating = true;
        const response = await api.post(
          `/ai/smart/summary/${this.resident_id}/recalculate-nutrition-risk`
        );
        if (response.data.success) {
          this.nutritionRisk = response.data.nutrition_risk;
          this.$toast.success("Score nutricional recalculado com sucesso!");
        } else {
          this.$toast.error("Erro ao recalcular score.");
        }
      } catch (error) {
        console.error("[NutritionRiskBadge] erro ao recalcular:", error);
        this.$toast.error("Erro ao recalcular. Tente novamente.");
      } finally {
        this.recalculating = false;
      }
    },

    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<style scoped>
/* ============================================================
   Wrapper e Badge
   ============================================================ */
.nutrition-risk-wrapper {
  display: inline-block;
  margin-top: 4px;
}

.nutrition-risk-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  user-select: none;
}

.nutrition-risk-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nutrition-risk-score {
  background: rgba(255, 255, 255, 0.9);
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 2px;
}

.badge-verde {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.badge-amarelo {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #92400e;
  border: 1px solid #f59e0b;
}

.badge-vermelho {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  border: 1px solid #f87171;
}

.badge-sem-dados {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
}

.nutrition-risk-loading {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
  cursor: default;
}

.nutrition-risk-loading:hover {
  transform: none;
  box-shadow: none;
}

/* ============================================================
   Overlay e Modal
   ============================================================ */
.nutrition-risk-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.nutrition-risk-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.nutrition-risk-modal-content {
  background: white;
  border-radius: 16px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ============================================================
   Header do Modal
   ============================================================ */
.nutrition-risk-modal-header {
  padding: 20px 24px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.nutrition-risk-modal-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nutrition-risk-modal-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
}

.header-verde {
  background: linear-gradient(135deg, #059669, #047857);
}
.header-amarelo {
  background: linear-gradient(135deg, #d97706, #b45309);
}
.header-vermelho {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}
.header-sem-dados {
  background: linear-gradient(135deg, #64748b, #475569);
}

.nutrition-risk-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
  line-height: 1;
}

.nutrition-risk-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* ============================================================
   Alerta de peso desatualizado
   ============================================================ */
.nutrition-missing-weight {
  margin: 20px 24px;
  padding: 14px 16px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #92400e;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

/* ============================================================
   Classificação
   ============================================================ */
.nutrition-risk-classification {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
}

.nutrition-risk-classification-icon {
  font-size: 2rem;
}

.nutrition-risk-classification h2 {
  font-size: 1.8rem;
  font-weight: 700;
}

.classification-verde {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  color: #065f46;
}
.classification-amarelo {
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  color: #92400e;
}
.classification-vermelho {
  background: linear-gradient(135deg, #fff1f2, #fee2e2);
  color: #991b1b;
}

/* ============================================================
   Detalhamento
   ============================================================ */
.nutrition-risk-detail-section {
  padding: 20px 24px;
}

.nutrition-risk-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.nutrition-risk-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.nutrition-risk-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.item-ok {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}
.item-alert {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.detail-label {
  color: #374151;
  flex: 1;
  line-height: 1.5;
}

.detail-sub {
  color: #6b7280;
  font-size: 12px;
  margin-left: 4px;
}

.detail-absent {
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
}

.detail-points {
  font-weight: 700;
  font-size: 14px;
  min-width: 32px;
  text-align: right;
  padding-top: 1px;
}

.points-ok {
  color: #059669;
}
.points-alert {
  color: #d97706;
}

/* ============================================================
   Nota de exclusividade
   ============================================================ */
.nutrition-exclusivity-note {
  font-size: 12px;
  color: #6b7280;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
}

/* ============================================================
   Total
   ============================================================ */
.nutrition-risk-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 12px;
}

.total-verde {
  background: #d1fae5;
  color: #065f46;
}
.total-amarelo {
  background: #fef3c7;
  color: #92400e;
}
.total-vermelho {
  background: #fee2e2;
  color: #991b1b;
}

.nutrition-risk-total-value {
  font-size: 18px;
  font-weight: 700;
}

/* ============================================================
   Legenda
   ============================================================ */
.nutrition-risk-legend {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.legend-item {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.legend-verde {
  background: #d1fae5;
  color: #065f46;
}
.legend-amarelo {
  background: #fef3c7;
  color: #92400e;
}
.legend-vermelho {
  background: #fee2e2;
  color: #991b1b;
}

/* ============================================================
   Footer
   ============================================================ */
.nutrition-risk-modal-footer {
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.nutrition-risk-recalculate-btn {
  background: #0f65b0 !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* ============================================================
   Responsivo
   ============================================================ */
@media (max-width: 576px) {
  .nutrition-risk-modal {
    padding: 10px;
  }

  .nutrition-risk-modal-content {
    max-height: 95vh;
  }

  .nutrition-risk-modal-footer {
    flex-direction: column;
    text-align: center;
  }
}
</style>